import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap, cx } from '@atlaskit/css';
import { useFlags } from '@atlaskit/flag/use-flags';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import type { AuthProvider } from '@atlaskit/media-core/auth';
import { MediaImage } from '@atlaskit/media-image';
import { Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useProductDependencies } from '../../../../../controllers/use-product-dependencies';
import { PlaceholderThumbnail } from '../placeholder';

import { messages } from './messages';

const IMAGE_CONFIGURATION = {
	allowAnimated: true,
	mode: 'full-fit' as const,
	upscale: false,
};

const styles = cssMap({
	base: {
		height: '40px',
		width: '44px',
		borderRadius: token('radius.small'),
		border: 'none',
		position: 'relative',
		backgroundSize: 'cover',
		backgroundPosition: 'center',
		backgroundRepeat: 'no-repeat',
	},
	selected: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		boxShadow: `inset 0 0 0 2px ${token('color.border.selected')}` as any,
	},
});

type UploadedImageThumbnailProps = {
	children?: React.ReactNode;
	collectionName?: string;
	contentId: string;
	imageId: string;
	isSelected: boolean;
	mediaAuthProvider?: AuthProvider;
	mediaConfigOverride?: {
		authProvider?: AuthProvider;
		collection?: string;
	};
	onClick: () => void;
};

export const UploadedImageThumbnail = ({
	contentId,
	imageId,
	isSelected,
	onClick,
	children,
	mediaConfigOverride,
}: UploadedImageThumbnailProps): React.JSX.Element => {
	const { showFlag } = useFlags();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { useMediaConfig } = useProductDependencies();

	const { authProvider, collection, fileId } =
		useMediaConfig?.({ contentId, imageSrc: imageId }) || {};

	let resolvedAuthProvider = authProvider;
	let resolvedCollection = collection;

	if (mediaConfigOverride?.authProvider && mediaConfigOverride?.collection) {
		resolvedAuthProvider = mediaConfigOverride.authProvider;
		resolvedCollection = mediaConfigOverride.collection;
	}

	const identifier = useMemo(
		() => ({
			mediaItemType: 'file' as const,
			id: fileId ?? '',
			collectionName: resolvedCollection,
		}),
		[fileId, resolvedCollection],
	);

	if (!resolvedAuthProvider || !resolvedCollection || !fileId) {
		return <PlaceholderThumbnail />;
	}

	return (
		<MediaImage
			//force rerender image on id change until https://product-fabric.atlassian.net/browse/CXP-2899 is fixed
			key={fileId}
			identifier={identifier}
			mediaClientConfig={{ authProvider: resolvedAuthProvider }}
			apiConfig={IMAGE_CONFIGURATION}
		>
			{({ data, loading, error }) => {
				if (loading || !data) {
					return <PlaceholderThumbnail />;
				}

				if (error) {
					void showFlag({
						id: 'upload-image-error',
						title: formatMessage(messages.uploadImageFailedTitle),
						description: formatMessage(messages.uploadImageFailedDescription),
						icon: <StatusErrorIcon label="" color={token('color.icon.danger')} />,
					});

					createAnalyticsEvent({
						type: 'error',
						data: {
							actionSubject: 'image',
							actionSubjectId: 'coverImageSelected',
							source: 'coverImageSelectedComponent',
						},
					}).fire();

					return <PlaceholderThumbnail />;
				}
				return (
					<Pressable
						xcss={cx(styles.base, isSelected && styles.selected)}
						style={{ backgroundImage: `url(${data.src})` }}
						onClick={onClick}
						testId="uploaded-image-thumbnail"
					>
						{children}
					</Pressable>
				);
			}}
		</MediaImage>
	);
};
